import React, { useState, useEffect } from 'react';
import './CaseStudies.css';

/* ── Icons ──────────────────────────────────────────────── */
const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/>
    <line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const SpinnerIcon = () => (
  <svg className="cs-spin" width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5">
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
  </svg>
);

const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

/* ── Skeleton card ──────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="cs-card cs-card--skeleton">
    <div className="cs-card__header">
      <div className="cs-skel cs-skel--tag" />
      <div className="cs-skel cs-skel--count" />
    </div>
    <div className="cs-card__body">
      <div className="cs-skel cs-skel--title" />
      <div className="cs-skel cs-skel--line" />
      <div className="cs-skel cs-skel--line cs-skel--short" />
    </div>
    <div className="cs-card__footer">
      <div className="cs-skel cs-skel--btn" />
      <div className="cs-skel cs-skel--btn" />
    </div>
  </div>
);

function CaseStudies() {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/case-study`);
      const result = await res.json();
      if (result.success) setInsights(result.data);
    } catch (err) {
      console.error('Error fetching case studies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (insight) => {
    setSelectedInsight(insight);
    setPdfLoading(true);
    setShowViewModal(true);
    
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/insights/${insight.type}/${insight.slug}`);
      const result = await res.json();
      if (result.success) {
        setSelectedInsight(result.data);
      }
    } catch (err) {
      console.error('Error fetching insight details:', err);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadRequest = (insight) => {
    setSelectedInsight(insight);
    setShowDownloadModal(true);
    setError('');
    setSuccess(false);
    setFormData({ name: '', email: '' });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  // FIXED: Updated download handler to properly download PDF files
  const handleDownload = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    const emailRx = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRx.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setDownloading(true);
    setError('');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/insights/${selectedInsight.type}/${selectedInsight.slug}/download`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Download failed');
      }

      // Get the file as blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedInsight.fileName || 'document.pdf';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccess(true);
      setTimeout(() => {
        setShowDownloadModal(false);
        setSuccess(false);
        fetchInsights();
      }, 1800);
    } catch (err) {
      console.error('Download error:', err);
      setError(err.message || 'Network error. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const closeModals = () => {
    if (!downloading) {
      setShowViewModal(false);
      setShowDownloadModal(false);
      setError('');
      setSuccess(false);
      setSelectedInsight(null);
      setPdfLoading(false);
    }
  };

  return (
    <div className="cs-page">
      <div className="cs-hero">
        <div className="cs-hero__inner">
          <p className="cs-hero__eyebrow">BaiRees Global Advisory</p>
          <h1 className="cs-hero__title">Case Studies</h1>
          <p className="cs-hero__sub">
            Discover how we've helped enterprises transform with innovative strategies,
            AI-led operations, and scalable digital ecosystems.
          </p>
        </div>
      </div>

      <div className="cs-body">
        {loading ? (
          <div className="cs-grid">
            {[1, 2, 3].map((n) => <SkeletonCard key={n} />)}
          </div>
        ) : insights.length === 0 ? (
          <div className="cs-empty">
            <p>No case studies available at this time.</p>
          </div>
        ) : (
          <div className="cs-grid">
            {insights.map((insight) => (
              <div key={insight._id} className="cs-card">
                <div className="cs-card__header">
                  <span className="cs-tag">Case Study</span>
                  <span className="cs-downloads">
                    <DownloadIcon />
                    {insight.downloadCount}
                  </span>
                </div>
                <div className="cs-card__body">
                  <h3 className="cs-card__title">{insight.title}</h3>
                  <p className="cs-card__desc">{insight.excerpt}</p>
                  {insight.tags && insight.tags.length > 0 && (
                    <div className="cs-card__tags">
                      {insight.tags.map((tag, i) => (
                        <span key={i} className="cs-tag-sm">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="cs-card__footer">
                  <button
                    className="cs-btn cs-btn--view"
                    onClick={() => handleView(insight)}
                  >
                    <ViewIcon />
                    View Case Study
                  </button>
                  <button
                    className="cs-btn cs-btn--download"
                    onClick={() => handleDownloadRequest(insight)}
                  >
                    <DownloadIcon />
                    Download PDF
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* View Modal with Iframe PDF Viewer */}
      {showViewModal && selectedInsight && (
        <div className="cs-overlay" onClick={closeModals}>
          <div className="cs-modal cs-modal--pdf" onClick={(e) => e.stopPropagation()}>
            <div className="cs-modal__header">
              <div>
                <p className="cs-modal__eyebrow">Case Study</p>
                <h2 className="cs-modal__title">{selectedInsight.title}</h2>
              </div>
              <button className="cs-modal__close" onClick={closeModals}>
                <CloseIcon />
              </button>
            </div>
            <div className="cs-modal__body cs-modal__body--pdf">
              {pdfLoading ? (
                <div className="cs-pdf-loading">
                  <div className="cs-spinner"></div>
                  <p>Loading PDF viewer...</p>
                </div>
              ) : selectedInsight.fileUrl ? (
                <iframe
                  src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedInsight.fileUrl)}&embedded=true`}
                  title={selectedInsight.title}
                  className="cs-pdf-iframe"
                  frameBorder="0"
                  allowFullScreen
                />
              ) : (
                <div className="cs-pdf-error">
                  <p>PDF viewer not available. Please download the file instead.</p>
                  <button 
                    className="cs-btn cs-btn--download"
                    onClick={() => {
                      setShowViewModal(false);
                      handleDownloadRequest(selectedInsight);
                    }}
                  >
                    <DownloadIcon />
                    Download PDF
                  </button>
                </div>
              )}
            </div>
            <div className="cs-modal__footer">
              <div className="cs-view-stats">
                <span>👁️ {selectedInsight.viewCount} views</span>
                <span>📥 {selectedInsight.downloadCount} downloads</span>
              </div>
              <button 
                className="cs-btn cs-btn--download"
                onClick={() => {
                  setShowViewModal(false);
                  handleDownloadRequest(selectedInsight);
                }}
              >
                <DownloadIcon />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download Modal - Requires name and email */}
      {showDownloadModal && (
        <div className="cs-overlay" onClick={closeModals}>
          <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cs-modal__header">
              <div>
                <p className="cs-modal__eyebrow">Download Case Study</p>
                <h2 className="cs-modal__title">{selectedInsight?.title}</h2>
              </div>
              <button className="cs-modal__close" onClick={closeModals} disabled={downloading}>
                <CloseIcon />
              </button>
            </div>
            {success ? (
              <div className="cs-modal__success">
                <div className="cs-modal__success-icon">✓</div>
                <p>Your download has started!</p>
                <p className="cs-modal__success-sub">Thank you, {formData.name}!</p>
              </div>
            ) : (
              <div className="cs-modal__body">
                <p className="cs-modal__intro">
                  Please provide your details to download this case study.
                </p>
                {error && <div className="cs-modal__error">{error}</div>}
                <form onSubmit={handleDownload}>
                  <div className="cs-field">
                    <label className="cs-field__label">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      disabled={downloading}
                      required
                    />
                  </div>
                  <div className="cs-field">
                    <label className="cs-field__label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      disabled={downloading}
                      required
                    />
                  </div>
                  <button type="submit" className="cs-btn cs-btn--submit" disabled={downloading}>
                    {downloading ? <><SpinnerIcon /> Processing…</> : <><DownloadIcon /> Download Now</>}
                  </button>
                </form>
                <p className="cs-modal__privacy">
                  Your details are used only to deliver this resource and will not be shared.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseStudies;